/**
 * One sales row in full (Sales records → click "Order / source"): what was sold, where the row came from
 * (marketplace API, uploaded file or manual entry), the order id with its returns, and the original
 * marketplace code it was matched from. Read-only: a wrong upload is fixed with Uploads → Undo.
 */
import React from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { platformLabel } from '../../config/platforms';
import { formatDateRange, number } from './format';
import { queryString, useAbortableGet } from './useAbortableGet';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const MATCH_TEXT = { master: 'SKU code', old_code: 'Old SKU code', identifier: 'Marketplace identifier (ASIN, FSN…)',
  mapped: 'Code mapping', rule: 'Same code with a listing suffix' };

export const rowSource = sale => {
  if (sale.integration_id) return { kind: 'api', label: sale.import_filename || 'Marketplace API' };
  if (sale.import_fingerprint) return { kind: 'file', label: sale.import_filename || 'Uploaded file' };
  return { kind: 'manual', label: 'Manual entry' };
};

const when = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value)
    : date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Item = ({ label, children, testId }) => (children === null || children === undefined || children === '' ? null : (
  <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-gray-100 py-2 text-[13px]" data-testid={testId}>
    <dt className="text-gray-500">{label}</dt>
    <dd className="min-w-0 break-words text-gray-900">{children}</dd>
  </div>
));

const OrderReturns = ({ orderId, country }) => {
  const state = useAbortableGet(orderId ? `${API}/sales/order-returns?${queryString({ order_id: orderId, country })}` : null);
  if (!orderId) return null;
  const list = state.data?.returns || [];
  return (
    <section className="mt-5" aria-labelledby="sales-row-returns">
      <h3 id="sales-row-returns" className="text-[13px] font-semibold text-gray-900">Returns on this order</h3>
      {state.loading ? <p className="mt-2 text-[12px] text-gray-500">Loading…</p>
        : state.error ? <p className="mt-2 text-[12px] text-red-700">{String(state.error)}</p>
          : list.length === 0 ? <p className="mt-2 text-[12px] text-gray-500" data-testid="sales-row-no-returns">No returns logged for this order.</p>
            : (
              <ul className="mt-2 space-y-1.5" data-testid="sales-row-returns">
                {list.map((r, i) => (
                  <li key={`${r.marketplace_return_id || i}`} className="rounded-lg border border-gray-200 px-3 py-2 text-[12px]">
                    <span className="font-medium">{number(r.quantity)} × {r.sku_code}</span>
                    {r.return_date && <span className="text-gray-500"> · {formatDateRange(r.return_date, r.return_date, true)}</span>}
                    {r.return_reason && <span className="text-gray-700"> · {r.return_reason}</span>}
                    {r.status && <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">{r.status}</span>}
                  </li>
                ))}
              </ul>
            )}
    </section>
  );
};

export const SalesRowDrawer = ({ sale, country, currency = '₹', canEdit = false, onClose, onFilter, returnFocus }) => {
  const open = Boolean(sale);
  const source = sale ? rowSource(sale) : null;
  const rupees = value => `${currency}${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const copy = text => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Order ID copied'), () => toast.error('Could not copy'));
  };
  return (
    <Sheet open={open} onOpenChange={next => { if (!next) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="sales-row-drawer"
        onCloseAutoFocus={e => {
          e.preventDefault();
          // The row may have left the table ("Show only this SKU"): then the table takes the focus.
          (returnFocus?.current?.isConnected ? returnFocus.current : document.querySelector('[data-testid="sales-table"]'))?.focus?.();
        }}>
        {sale && (
          <>
            <SheetHeader>
              <SheetTitle className="text-[17px]">{sale.product_name || sale.sku_code}</SheetTitle>
              <SheetDescription>
                {formatDateRange(sale.date, sale.date, true)} · {platformLabel(sale.platform)} · {number(sale.units_sold)} unit{sale.units_sold === 1 ? '' : 's'}
              </SheetDescription>
            </SheetHeader>
            <dl className="mt-4">
              <Item label="SKU">
                <span className="font-mono text-[12px]">{sale.sku_code}</span>
                {onFilter && (
                  <button type="button" className="ml-2 text-[12px] text-blue-700 hover:underline" onClick={() => { onFilter('sku', sale.sku_code); onClose(); }}>
                    Show only this SKU
                  </button>
                )}
              </Item>
              <Item label="Brand · category">{[sale.brand, sale.category].filter(Boolean).join(' · ') || null}</Item>
              <Item label="Sale value" testId="sales-row-value">{rupees(sale.sale_value ?? sale.revenue)}</Item>
              <Item label="Selling price">{rupees(sale.effective_price)} per unit{sale.base_price ? ` (list ${rupees(sale.base_price)}${sale.discount_pct ? `, ${sale.discount_pct > 0 ? '−' : '+'}${Math.abs(sale.discount_pct)}%` : ''})` : ''}</Item>
              <Item label="Order ID" testId="sales-row-order">
                {sale.order_id ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-mono text-[12px]">{sale.order_id}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label="Copy order ID" onClick={() => copy(sale.order_id)}>
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </span>
                ) : <span className="text-gray-500">Not in the source (only marketplace API rows carry order IDs today)</span>}
              </Item>
              <Item label="Fulfilment">{sale.fulfilment || null}</Item>
              <Item label="Source" testId="sales-row-source">
                {source.label}
                <span className="ml-1 text-gray-500">({source.kind === 'api' ? 'marketplace API' : source.kind === 'file' ? 'uploaded file' : 'added by hand'})</span>
              </Item>
              <Item label="Added">{[when(sale.imported_at || sale.created_at), sale.imported_by || sale.created_by].filter(Boolean).join(' by ') || null}</Item>
              <Item label="Marketplace code">{sale.source_code && sale.source_code !== sale.sku_code ? <span className="font-mono text-[12px]">{sale.source_code}</span> : null}</Item>
              <Item label="ASIN / alt code">{(sale.alt_codes || []).filter(Boolean).join(', ') || null}</Item>
              <Item label="Matched by">{sale.match_type ? (MATCH_TEXT[sale.match_type] || sale.match_type) : null}</Item>
            </dl>
            {source.kind === 'file' && canEdit && (
              <p className="mt-4 rounded-lg bg-gray-50 p-3 text-[12px] text-gray-600">
                Wrong data in this file? Use <span className="font-medium">Uploads → Undo</span> to remove the whole file, then upload a corrected one.
              </p>
            )}
            <OrderReturns orderId={sale.order_id} country={country} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SalesRowDrawer;
